import posthog from 'posthog-js'
import type { CaptureResult } from 'posthog-js'
import { PostHogProvider } from 'posthog-js/react'
import { useEffect } from 'react'
import type { PropsWithChildren } from 'react'
import { ENV } from '../config/env'

const URL_PROPERTIES = [
  '$current_url',
  '$initial_current_url',
  '$referrer',
  '$initial_referrer',
] as const
const MCP_OAUTH_PARAMETERS = [
  'code',
  'state',
  'error',
  'error_description',
] as const

function scrubMcpOauthUrl(value: unknown): unknown {
  if (typeof value !== 'string') return value
  try {
    const url = new URL(value)
    const isOauthCallback =
      url.pathname === '/tools' &&
      url.searchParams.has('state') &&
      (url.searchParams.has('code') || url.searchParams.has('error'))
    if (!isOauthCallback) return value
    MCP_OAUTH_PARAMETERS.forEach((parameter) =>
      url.searchParams.delete(parameter)
    )
    return url.toString()
  } catch {
    return value
  }
}

function scrubUrlProperties(
  properties: CaptureResult['properties'] | undefined
) {
  if (!properties) return properties
  const scrubbed = { ...properties }
  URL_PROPERTIES.forEach((property) => {
    scrubbed[property] = scrubMcpOauthUrl(scrubbed[property])
  })
  return scrubbed
}

export function scrubMcpOauthUrls(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event) return null
  return {
    ...event,
    properties: scrubUrlProperties(event.properties) ?? {},
    ...(event.$set ? { $set: scrubUrlProperties(event.$set) } : {}),
    ...(event.$set_once
      ? { $set_once: scrubUrlProperties(event.$set_once) }
      : {}),
  }
}

const PostHogProviderWrapper = ({ children }: PropsWithChildren) => {
  useEffect(() => {
    if (ENV === 'development') return

    posthog.init(process.env.BUN_PUBLIC_POSTHOG_KEY || '', {
      api_host: process.env.BUN_PUBLIC_POSTHOG_HOST,
      person_profiles: 'identified_only',
      capture_pageview: true,
      capture_exceptions: true,
      before_send: scrubMcpOauthUrls,
    })

    posthog.register({ environment: ENV })
  }, [])

  if (ENV === 'development') return <>{children}</>

  return <PostHogProvider client={posthog}>{children}</PostHogProvider>
}

export default PostHogProviderWrapper
